/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * RollbackApplicationModal
 * -------------------------
 * Undoes the most recent pack update applied to one application. Simpler
 * than UpdateApplicationModal — there's no plan to review or conflicts to
 * resolve, just a confirmation and the result.
 *
 * Props:
 * - theme: Theme object (passed in, matches UpdateApplicationModal's convention).
 * - applicationId: The application's id (string or number).
 * - currentVersionCode: The application's current_version_code, as already
 *   loaded by the parent page — used only to show "version X -> version
 *   X-1" in the confirmation step (same current_version_code - 1 math the
 *   backend's rollback_last_update uses). No extra fetch just for this.
 * - onClose: Called when the modal should be dismissed.
 * - onRolledBack: Called once with the rollback response JSON, right after
 *   a successful rollback.
 */
import React, { useState } from 'react';
import APIHelper from '../../api/APIHelper';
import envConfig from '../../config/envConfig';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

export default function RollbackApplicationModal({ theme, applicationId, currentVersionCode, onClose, onRolledBack }) {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);
    const [step, setStep] = useState('confirm'); // confirm | rolling_back | error
    const [errorMessage, setErrorMessage] = useState('');
    const targetVersionCode = currentVersionCode - 1;

    React.useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 991);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleRollback = async () => {
        setStep('rolling_back');
        try {
            const resp = await apiHelper.apiCall(`/arcee/application/${applicationId}/rollback-update/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            const json = await resp.json();
            if (!resp.ok) {
                setErrorMessage(json.error || json.message || 'Failed to roll back.');
                setStep('error');
                return;
            }
            // Close immediately on success rather than showing a separate
            // result screen — the version badge on the page updates via
            // onRolledBack's query invalidation, and a second rollback (if
            // wanted) is a deliberate new click on the Rollback button, not
            // something this same modal session should chain into.
            onRolledBack && onRolledBack(json);
            onClose();
        } catch (e) {
            setErrorMessage(e.message || 'Failed to roll back.');
            setStep('error');
        }
    };

    return (
        <div
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 3000,
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                padding: isMobile ? '0.5rem' : '1rem', overflow: 'auto',
            }}
            onClick={step === 'rolling_back' ? undefined : onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="rollback-application-title"
                onClick={e => e.stopPropagation()}
                style={{
                    background: theme.footer, borderRadius: theme.cardRadius,
                    width: isMobile ? '98%' : '90%', maxWidth: '600px', maxHeight: '90vh',
                    overflowY: 'auto', overflowX: 'hidden',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                    padding: isMobile ? '1rem' : '1.5rem', color: theme.text,
                    fontFamily: 'system-ui, sans-serif', display: 'flex', flexDirection: 'column',
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h2 id="rollback-application-title" style={{ margin: 0, fontWeight: 700, fontSize: isMobile ? '1.25rem' : '1.5rem', color: theme.primary }}>
                        Roll Back Update
                    </h2>
                    {step !== 'rolling_back' && (
                        <button onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'none', fontSize: '1.7rem', fontWeight: 'bold', color: theme.secondary, cursor: 'pointer' }}>
                            &times;
                        </button>
                    )}
                </div>

                {step === 'confirm' && (
                    <div>
                        <p>
                            Roll back from version <strong>{currentVersionCode}</strong> to version <strong>{targetVersionCode}</strong>?
                        </p>
                        <p>This undoes the most recent update pulled from the source package, reverting any table or field it changed back to how it looked before.</p>
                        <p className="text-muted small">This can't be undone — the same update can be pulled again later from Check for Updates if needed.</p>
                        <div className="d-flex justify-content-end gap-2">
                            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                            <button className="btn btn-danger" onClick={handleRollback}>Roll Back</button>
                        </div>
                    </div>
                )}

                {step === 'rolling_back' && <p>Rolling back…</p>}

                {step === 'error' && (
                    <div>
                        <p className="text-danger">{errorMessage}</p>
                        <div className="d-flex justify-content-end">
                            <button className="btn btn-secondary" onClick={onClose}>Close</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
